import { useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { Monitor, Moon, Sun } from 'lucide-react';
import { Button } from '@/components/ui-kit/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui-kit/dropdown-menu';
import { useTheme } from '@/styles/theme/theme-provider';

type ThemeChoice = 'system' | 'light' | 'dark';

/** System, Light, Dark, in the prototype's order. */
export const THEME_OPTIONS = [
  { value: 'system', label: 'SYSTEM', icon: Monitor },
  { value: 'light', label: 'LIGHT', icon: Sun },
  { value: 'dark', label: 'DARK', icon: Moon },
] as const;

const DARK_QUERY = '(prefers-color-scheme: dark)';
const subscribe = (onChange: () => void) => {
  const media = window.matchMedia(DARK_QUERY);
  media.addEventListener('change', onChange);
  return () => media.removeEventListener('change', onChange);
};

/** The theme in effect: the picked one, or the system's for "System". */
export const useEffectiveTheme = (): 'light' | 'dark' => {
  const { theme } = useTheme();
  const systemIsDark = useSyncExternalStore(subscribe, () => window.matchMedia(DARK_QUERY).matches);
  if (theme === 'system') return systemIsDark ? 'dark' : 'light';
  return theme;
};

/**
 * The top bar's theme switch (Component Map › Theme switch): a ghost icon button showing the theme in
 * effect (sun or moon) that opens System / Light / Dark as radio items.
 */
export const ThemeSwitcher = () => {
  const { t } = useTranslation();
  const { theme, setTheme } = useTheme();
  const effective = useEffectiveTheme();
  const Current = effective === 'dark' ? Moon : Sun;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-8 text-muted-foreground hover:text-foreground"
          aria-label={t('THEME')}
          title={t('THEME')}
        >
          <Current className="size-[18px]" />
        </Button>
      </DropdownMenuTrigger>
      {/* A small menu on phones too, not the full-width phone menu. */}
      <DropdownMenuContent align="end" className="min-w-40 max-sm:w-auto max-sm:min-w-40">
        <DropdownMenuLabel>{t('THEME')}</DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={theme}
          onValueChange={(next) => setTheme(next as ThemeChoice)}
        >
          {THEME_OPTIONS.map(({ value, label, icon: Icon }) => (
            <DropdownMenuRadioItem key={value} value={value}>
              <Icon className="size-4" />
              {t(label)}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
